import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useBilling } from '@/hooks/useBilling';
import { useTopupAuth } from '@/hooks/useTopupAuth';
import TopupPasswordModal from '@/components/admin/TopupPasswordModal';
import { DollarSign, ArrowLeft, CheckCircle2, AlertCircle, ShieldAlert, ShieldCheck } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

/**
 * Password-protected topup page
 *
 * Authentication requirements:
 * 1. Must be logged in as admin (ProtectedRoute)
 * 2. Must enter correct password (useTopupAuth)
 * 3. Must have super_admin role (role check in component)
 *
 * Hidden from admin sidebar menu - direct URL access only
 */
export default function TopupPage() {
  const navigate = useNavigate();
  const { adminUser } = useAdminAuth();
  const { balance, loading: balanceLoading, depositMoney } = useBilling();
  const { isPasswordAuthenticated, isLoading, validatePassword } = useTopupAuth();

  const [depositAmount, setDepositAmount] = useState<string>('');
  const [depositing, setDepositing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);
  const [togglingMaintenance, setTogglingMaintenance] = useState(false);

  const isSuperAdmin = adminUser?.role === 'super_admin';
  const LOW_BALANCE_THRESHOLD = 1200; // VND

  /**
   * Load maintenance mode status from Firestore
   */
  useEffect(() => {
    const loadMaintenanceMode = async () => {
      try {
        const docRef = doc(db, 'system_settings', 'maintenance_mode');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setMaintenanceMode(docSnap.data().enabled || false);
        }
      } catch (error) {
        console.error('Error loading maintenance mode:', error);
      } finally {
        setLoadingMaintenance(false);
      }
    };

    loadMaintenanceMode();
  }, []);

  /**
   * Toggle maintenance mode
   */
  const toggleMaintenanceMode = async () => {
    if (!adminUser || !isSuperAdmin) {
      alert('Chỉ super admin mới có quyền bật/tắt chế độ bảo trì');
      return;
    }

    const newMode = !maintenanceMode;
    const action = newMode ? 'BẬT' : 'TẮT';

    // If enabling maintenance, check if balance is low
    if (newMode && balance && balance.balance >= LOW_BALANCE_THRESHOLD) {
      const confirmMessage = `Số dư hiện tại (${balance.balance.toLocaleString()} VND) vẫn còn đủ.\n\nBạn có chắc muốn ${action} chế độ bảo trì?`;
      if (!confirm(confirmMessage)) {
        return;
      }
    } else if (newMode) {
      const confirmMessage = `⚠️ ${action} CHẾ ĐỘ BẢO TRÌ\n\nKhi bật, trang thiết kế sẽ hiển thị thông báo "Xin lỗi, trang thiết kế tạm thời không thể hoạt động" và người dùng sẽ bị chuyển hướng.\n\nXác nhận?`;
      if (!confirm(confirmMessage)) {
        return;
      }
    } else {
      if (!confirm(`${action} chế độ bảo trì?\n\nNgười dùng sẽ có thể truy cập trang thiết kế bình thường.`)) {
        return;
      }
    }

    try {
      setTogglingMaintenance(true);

      await setDoc(doc(db, 'system_settings', 'maintenance_mode'), {
        enabled: newMode,
        updated_at: new Date(),
        updated_by: adminUser.user.uid,
        reason: newMode && balance && balance.balance < LOW_BALANCE_THRESHOLD
          ? `Số dư thấp (${balance.balance} VND < ${LOW_BALANCE_THRESHOLD} VND)`
          : 'Manual toggle by admin'
      });

      setMaintenanceMode(newMode);
      alert(`Đã ${action} chế độ bảo trì thành công!`);
    } catch (error: any) {
      console.error('Error toggling maintenance mode:', error);
      alert(`Không thể ${action} chế độ bảo trì: ${error.message}`);
    } finally {
      setTogglingMaintenance(false);
    }
  };

  /**
   * Handle deposit submission
   */
  const handleDeposit = async () => {
    // Verify admin auth and role
    if (!adminUser || !isSuperAdmin) {
      alert('Chỉ super admin mới có quyền nạp tiền');
      return;
    }

    // Validate amount
    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Vui lòng nhập số tiền hợp lệ');
      return;
    }

    // Confirm before deposit
    if (!confirm(`Xác nhận nạp ${amount.toLocaleString('vi-VN')} VND?`)) {
      return;
    }

    try {
      setDepositing(true);
      setSuccessMessage(null);

      // Call deposit function from useBilling hook
      await depositMoney(amount, adminUser.user.uid);

      // Clear input and show success message
      setDepositAmount('');
      setSuccessMessage(`Đã nạp thành công ${amount.toLocaleString('vi-VN')} VND`);

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error: any) {
      alert(`Nạp tiền thất bại: ${error.message}`);
      console.error('Deposit error:', error);
    } finally {
      setDepositing(false);
    }
  };

  /**
   * Quick amount selection buttons
   */
  const quickAmounts = [100000, 500000, 1000000, 5000000];

  // Show loading while checking password session or fetching balance
  if (isLoading || balanceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">Đang tải...</div>
      </div>
    );
  }

  return (
    <>
      {/* Password Authentication Modal */}
      <TopupPasswordModal
        isOpen={!isPasswordAuthenticated}
        onPasswordSubmit={validatePassword}
      />

      {/* Main Page Content - Only visible if password authenticated */}
      {isPasswordAuthenticated && (
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="max-w-2xl mx-auto">
            {/* Header Section */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/admin/billing')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Quay lại Thanh toán</span>
              </button>

              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-1">
                <DollarSign className="w-8 h-8 text-indigo-600" />
                Nạp tiền hệ thống
              </h1>
              <p className="text-gray-600">
                Trang này chỉ dành cho super admin
              </p>
            </div>

            {/* Current Balance Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <h2 className="text-sm font-medium text-gray-600 mb-2">
                Số dư hiện tại
              </h2>
              <p className="text-3xl font-bold text-gray-900">
                {balance?.balance?.toLocaleString('vi-VN') ?? 0} VND
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Tổng đã nạp: {balance?.total_deposited?.toLocaleString('vi-VN') ?? 0} VND
              </p>

              {/* Low balance warning */}
              {balance && balance.balance < LOW_BALANCE_THRESHOLD && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-800 font-medium text-sm">
                      ⚠️ Số dư thấp (dưới {LOW_BALANCE_THRESHOLD.toLocaleString()} VND)
                    </p>
                    <p className="text-red-700 text-xs mt-1">
                      Vui lòng nạp tiền hoặc bật chế độ bảo trì để tránh lỗi.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Maintenance Mode Control - Only for super_admin */}
            {isSuperAdmin && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      {maintenanceMode ? (
                        <ShieldAlert className="w-6 h-6 text-red-600" />
                      ) : (
                        <ShieldCheck className="w-6 h-6 text-green-600" />
                      )}
                      Chế độ bảo trì
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {maintenanceMode
                        ? 'Trang thiết kế đang bị chặn và hiển thị thông báo bảo trì.'
                        : 'Trang thiết kế đang hoạt động bình thường.'}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    maintenanceMode
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {maintenanceMode ? '🔴 BẬT' : '🟢 TẮT'}
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Info box */}
                  <div className={`p-4 rounded-lg border ${
                    maintenanceMode
                      ? 'bg-red-50 border-red-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <p className={`text-sm font-medium ${
                      maintenanceMode ? 'text-red-900' : 'text-blue-900'
                    }`}>
                      {maintenanceMode ? (
                        <>
                          <strong>Trạng thái:</strong> Người dùng sẽ thấy thông báo "Xin lỗi, trang thiết kế tạm thời không thể hoạt động" và tự động redirect sau 5 giây.
                        </>
                      ) : (
                        <>
                          <strong>Mục đích:</strong> Sử dụng khi hết tiền (số dư &lt; {LOW_BALANCE_THRESHOLD.toLocaleString()} VND) để ngăn người dùng tạo designs mới.
                        </>
                      )}
                    </p>
                  </div>

                  {/* Toggle button */}
                  <button
                    onClick={toggleMaintenanceMode}
                    disabled={togglingMaintenance || loadingMaintenance}
                    className={`w-full px-6 py-3 font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                      maintenanceMode
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    {togglingMaintenance ? (
                      <>Đang xử lý...</>
                    ) : maintenanceMode ? (
                      <>
                        <ShieldCheck className="w-5 h-5" />
                        TẮT chế độ bảo trì
                      </>
                    ) : (
                      <>
                        <ShieldAlert className="w-5 h-5" />
                        BẬT chế độ bảo trì (Block trang thiết kế)
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
            )}

            {/* Deposit Form Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Nạp tiền vào hệ thống
              </h2>

              {/* Permission Check */}
              {!isSuperAdmin ? (
                // No permission message
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-yellow-800 font-medium">
                      Bạn không có quyền nạp tiền
                    </p>
                    <p className="text-yellow-700 text-sm mt-1">
                      Chỉ super admin mới được phép nạp tiền vào hệ thống.
                    </p>
                  </div>
                </div>
              ) : (
                // Deposit form for super_admin
                <div className="space-y-4">
                  {/* Amount Input */}
                  <div>
                    <label
                      htmlFor="deposit-amount"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Số tiền nạp (VND)
                    </label>
                    <input
                      id="deposit-amount"
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="Nhập số tiền"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-lg"
                      disabled={depositing}
                      min="0"
                      step="1000"
                    />
                  </div>

                  {/* Quick Amount Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {quickAmounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setDepositAmount(amount.toString())}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={depositing}
                        title={`${amount.toLocaleString('vi-VN')} VND`}
                      >
                        {(amount / 1000).toLocaleString('vi-VN')}K
                      </button>
                    ))}
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleDeposit}
                    disabled={depositing || !depositAmount}
                    className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {depositing ? 'Đang nạp...' : 'Xác nhận nạp tiền'}
                  </button>

                  {/* Help Text */}
                  <p className="text-xs text-gray-500 text-center">
                    Vui lòng kiểm tra kỹ số tiền trước khi xác nhận
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
